// ═══════════════════════════════════════════════════════════
// Documents Tab — upload, classify, and release documents
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconUpload, IconLoader2, IconFileText, IconCheck,
  IconAlertCircle, IconEye, IconDownload, IconCut, IconTrash,
  IconLink, IconCopy, IconX,
} from '@tabler/icons-react';
import {
  getDocumentsForRequest,
  uploadAndCreateDocument,
  classifyDocument,
  releaseDocument,
  unreleaseDocument,
  publishDocument,
  unpublishDocument,
  deleteFoiaDocument,
  getStaffDownloadUrl,
  getOriginalDownloadUrl,
  type FoiaDocument,
  type ResponsiveClass,
} from '../../../../api/foiaDocuments';
import {
  listTokensForRequest,
  createUploadToken,
  revokeToken,
  type UploadToken,
} from '../../../../api/uploadTokens';
import { getOrCreateReleaseToken, buildReleaseUrl } from '../../../../api/releaseTokens';
import { sendCorrespondence } from '../../../../api/correspondence';
import { createAuditEntry } from '../../../../api/auditLog';

interface Props {
  requestId:      number;
  actorEmail?:    string;
  requesterEmail?: string;
  requesterName?:  string;
  requestNumber?:  string;
}

const RESPONSIVE_OPTIONS: { value: ResponsiveClass; label: string }[] = [
  { value: 'unclassified',       label: 'Unclassified' },
  { value: 'responsive',         label: 'Responsive' },
  { value: 'partiallyResponsive', label: 'Partially Responsive' },
  { value: 'nonResponsive',      label: 'Non-Responsive' },
];

const RESPONSIVE_STYLE: Record<ResponsiveClass, { bg: string; text: string }> = {
  unclassified:        { bg: 'var(--cds-gray-50)', text: 'var(--color-neutral-500)' },
  responsive:          { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)' },
  partiallyResponsive: { bg: 'var(--cds-yellow-50)', text: 'var(--cds-yellow-500)' },
  nonResponsive:       { bg: 'var(--cds-red-50)', text: 'var(--cds-red-700)' },
};

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Strip the "<email>" portion from "Name <email>" format stored for upload-link uploads
function displayName(raw: string): string {
  return raw.replace(/<[^>]+>/, '').trim() || raw;
}

function DocRow({
  doc, actorEmail, onUpdate, onDelete, onReleased, requesterSubmitted,
}: {
  doc: FoiaDocument;
  actorEmail?: string;
  onUpdate: (d: FoiaDocument) => void;
  onDelete: (id: number) => void;
  onReleased?: (doc: FoiaDocument) => void;
  requesterSubmitted?: boolean;
}) {
  const navigate = useNavigate();
  const [classifying, setClassifying]   = useState(false);
  const [releasing, setReleasing]       = useState(false);
  const [publishing, setPublishing]     = useState(false);
  const [downloading, setDownloading]   = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const s = RESPONSIVE_STYLE[doc.responsiveClass];

  async function handleClassify(rc: ResponsiveClass) {
    setClassifying(true);
    try {
      const updated = await classifyDocument(doc.recordId, rc, [], actorEmail ?? '');
      onUpdate(updated);
    } finally {
      setClassifying(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete "${doc.fileName}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteFoiaDocument(doc.recordId);
      onDelete(doc.recordId);
    } finally {
      setDeleting(false);
    }
  }

  async function handleRelease() {
    if (!confirm(`Release "${doc.fileName}" to the requester?`)) return;
    setReleasing(true);
    try {
      const updated = await releaseDocument(doc.recordId, actorEmail ?? '');
      onUpdate(updated);
      onReleased?.(updated);
    } finally {
      setReleasing(false);
    }
  }

  async function handleUnrelease() {
    if (!confirm(`Unrelease "${doc.fileName}"? It will no longer be accessible via the download link.`)) return;
    setReleasing(true);
    try {
      const updated = await unreleaseDocument(doc.recordId, actorEmail ?? '');
      onUpdate(updated);
    } finally {
      setReleasing(false);
    }
  }

  async function handlePublishToggle() {
    setPublishing(true);
    try {
      const updated = doc.isPublished
        ? await unpublishDocument(doc, actorEmail ?? '')
        : await publishDocument(doc, actorEmail ?? '');
      onUpdate(updated);
    } finally {
      setPublishing(false);
    }
  }

  async function openUrl(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function handleDownload() {
    setDownloading(true);
    setDownloadError(null);
    try {
      // getStaffDownloadUrl prefers redacted version when available
      const url = await getStaffDownloadUrl(doc);
      if (!url) throw new Error('Could not get download URL — the file may need to be re-uploaded.');
      await openUrl(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setDownloading(false);
    }
  }

  async function handleDownloadOriginal() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const url = await getOriginalDownloadUrl(doc);
      if (!url) throw new Error('Could not get download URL for original.');
      await openUrl(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '10px 14px',
      borderBottom: '1px solid var(--color-neutral-100)',
    }}>
      {/* Icon */}
      <IconFileText size={20} style={{ color: 'var(--color-neutral-300)', flexShrink: 0 }} />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {doc.fileName}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{formatBytes(doc.fileSize)}</span>
          <span style={{ opacity: 0.3 }}>·</span>
          {doc.source === 'upload-link' ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--cds-violet-700)', fontWeight: 500 }}>
              <IconLink size={10} />
              {doc.uploadedBy ? displayName(doc.uploadedBy) : 'Upload link'}
            </span>
          ) : (
            <span>
              <span style={{ textTransform: 'capitalize' }}>{doc.source}</span>
              {doc.uploadedBy && (
                <span style={{ color: 'var(--color-neutral-600)', fontWeight: 500 }}> · {displayName(doc.uploadedBy)}</span>
              )}
            </span>
          )}
          {doc.uploadedAt && <><span style={{ opacity: 0.3 }}>·</span><span>{new Date(doc.uploadedAt).toLocaleDateString()}</span></>}
          {doc.isReleased && (
            <span style={{ color: 'var(--cds-green-700)', display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconCheck size={10} /> Released
            </span>
          )}
        </div>
      </div>

      {/* Responsive class */}
      {classifying ? (
        <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} />
      ) : (
        <select
          style={{
            fontSize: 12, fontWeight: 500, padding: '3px 8px', borderRadius: 4,
            border: `1px solid ${s.bg}`, background: s.bg, color: s.text,
            cursor: 'pointer',
          }}
          value={doc.responsiveClass}
          onChange={e => handleClassify(e.target.value as ResponsiveClass)}
        >
          {RESPONSIVE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button
          className="btn btn-secondary btn-sm"
          style={{ padding: '3px 8px', gap: 3, fontSize: 12 }}
          onClick={handleDownload}
          disabled={downloading}
          title={doc.redactedStorageUrl ? 'Download redacted version' : 'Download original'}
        >
          {downloading
            ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
            : <IconDownload size={11} />
          }
          {doc.redactedStorageUrl ? 'Redacted' : ''}
        </button>
        {doc.redactedStorageUrl && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ padding: '3px 8px', gap: 3, fontSize: 12, opacity: 0.7 }}
            onClick={handleDownloadOriginal}
            disabled={downloading}
            title="Download original (unredacted)"
          >
            <IconDownload size={11} /> Orig
          </button>
        )}
        {!requesterSubmitted && doc.mimeType === 'application/pdf' && !doc.isReleased && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ padding: '3px 8px', gap: 3, fontSize: 12 }}
            onClick={() => navigate(`/staff/redact/${doc.recordId}`)}
            title="Open in redaction editor"
          >
            <IconCut size={11} /> Redact
          </button>
        )}
        {!requesterSubmitted && (doc.mimeType === 'text/csv' || doc.fileName.toLowerCase().endsWith('.csv')) && !doc.isReleased && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ padding: '3px 8px', gap: 3, fontSize: 12 }}
            onClick={() => navigate(`/staff/redact-csv/${doc.recordId}`)}
            title="Open in spreadsheet redaction editor"
          >
            <IconCut size={11} /> Redact
          </button>
        )}
        {!requesterSubmitted && !doc.isReleased && doc.responsiveClass !== 'nonResponsive' && doc.responsiveClass !== 'unclassified' && (
          <button
            className="btn btn-primary btn-sm"
            style={{ padding: '3px 8px', gap: 3, fontSize: 12 }}
            onClick={handleRelease}
            disabled={releasing}
            title="Release to requester"
          >
            {releasing
              ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              : <><IconEye size={11} /> Release</>
            }
          </button>
        )}
        {!requesterSubmitted && doc.isReleased && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ padding: '3px 8px', gap: 3, fontSize: 12, color: 'var(--cds-yellow-700)' }}
            onClick={handleUnrelease}
            disabled={releasing}
            title="Unrelease — remove from requester download link"
          >
            {releasing
              ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              : <><IconX size={11} /> Unrelease</>
            }
          </button>
        )}
        {!requesterSubmitted && doc.isReleased && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ padding: '3px 8px', gap: 3, fontSize: 12, color: doc.isPublished ? 'var(--cds-green-700)' : 'var(--color-neutral-600)' }}
            onClick={handlePublishToggle}
            disabled={publishing}
            title={doc.isPublished ? 'Remove from Public Records Library' : 'Add to Public Records Library'}
          >
            {publishing
              ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              : doc.isPublished
                ? <><IconCheck size={11} /> Library</>
                : 'Add to Library'
            }
          </button>
        )}
        <button
          className="btn btn-secondary btn-sm"
          style={{ padding: '3px 8px', gap: 3, fontSize: 12, color: 'var(--cds-red-700)' }}
          onClick={handleDelete}
          disabled={deleting}
          title="Delete document"
        >
          {deleting
            ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
            : <IconTrash size={11} />
          }
        </button>
      </div>
      {downloadError && (
        <div style={{ gridColumn: '1/-1', fontSize: 12, color: 'var(--cds-red-700)', padding: '0 14px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconAlertCircle size={11} /> {downloadError}
        </div>
      )}
    </div>
  );
}

// ── Upload Links modal ───────────────────────────────────

const EXPIRY_OPTIONS = [
  { days: 7,  label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days (default)' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
];

function tokenUrl(token: string) {
  return `${window.location.origin}/upload/${token}`;
}

function daysUntil(iso: string) {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

interface UploadLinksModalProps {
  requestId: number;
  actorEmail: string;
  onClose: () => void;
}

function UploadLinksModal({ requestId, actorEmail, onClose }: UploadLinksModalProps) {
  const [tokens, setTokens]         = useState<UploadToken[]>([]);
  const [loading, setLoading]       = useState(true);
  const [label, setLabel]           = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [creating, setCreating]     = useState(false);
  const [newToken, setNewToken]     = useState<UploadToken | null>(null);
  const [copiedId, setCopiedId]     = useState<number | null>(null);
  const [revoking, setRevoking]     = useState<number | null>(null);

  useEffect(() => {
    listTokensForRequest(requestId)
      .then(setTokens)
      .finally(() => setLoading(false));
  }, [requestId]);

  async function handleCreate() {
    setCreating(true);
    try {
      const t = await createUploadToken({ requestId, label, expiryDays, createdBy: actorEmail });
      setTokens(prev => [t, ...prev]);
      setNewToken(t);
      setLabel('');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(t: UploadToken) {
    if (!confirm('Revoke this link? Anyone with the URL will no longer be able to upload.')) return;
    setRevoking(t.recordId);
    try {
      await revokeToken(t.recordId);
      setTokens(prev => prev.map(x => x.recordId === t.recordId ? { ...x, isActive: false } : x));
      if (newToken?.recordId === t.recordId) setNewToken(null);
    } finally {
      setRevoking(null);
    }
  }

  function copyUrl(t: UploadToken) {
    navigator.clipboard.writeText(tokenUrl(t.token));
    setCopiedId(t.recordId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const active   = tokens.filter(t => t.isActive);
  const inactive = tokens.filter(t => !t.isActive);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'white', borderRadius: 4, width: 520, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid var(--color-neutral-100)' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-900)' }}>Upload Links</div>
            <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>Share a link so others can upload files without an account</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', padding: 4 }}>
            <IconX size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* New link form */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: 10 }}>Generate New Link</div>
            <textarea
              className="form-input"
              style={{ width: '100%', fontSize: 14, minHeight: 72, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Instructions for the uploader — e.g. 'Please upload all budget documents from Q1 2024, including any supporting spreadsheets.'"
              value={label}
              onChange={e => setLabel(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <select
                className="form-input"
                style={{ fontSize: 12, padding: '6px 10px' }}
                value={expiryDays}
                onChange={e => setExpiryDays(Number(e.target.value))}
              >
                {EXPIRY_OPTIONS.map(o => (
                  <option key={o.days} value={o.days}>Expires in {o.label}</option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-sm"
                style={{ gap: 5, whiteSpace: 'nowrap' }}
                onClick={handleCreate}
                disabled={creating}
              >
                {creating
                  ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  : <IconLink size={12} />
                }
                Generate Link
              </button>
            </div>
          </div>

          {/* Newly created — highlight */}
          {newToken && (
            <div style={{ background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cds-green-700)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                <IconCheck size={12} /> Link created — copy and share it
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <code style={{ flex: 1, fontSize: 12, background: 'white', border: '1px solid var(--cds-green-200)', borderRadius: 5, padding: '6px 10px', color: 'var(--cds-green-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tokenUrl(newToken.token)}
                </code>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ gap: 4, flexShrink: 0 }}
                  onClick={() => copyUrl(newToken)}
                >
                  {copiedId === newToken.recordId ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  {copiedId === newToken.recordId ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Existing active links */}
          {loading && (
            <div style={{ textAlign: 'center', color: 'var(--color-neutral-400)', padding: '20px 0' }}>
              <IconLoader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {!loading && active.filter(t => t.recordId !== newToken?.recordId).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Active Links</div>
              {active.filter(t => t.recordId !== newToken?.recordId).map(t => (
                <div key={t.recordId} style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: '10px 12px', marginBottom: 8 }}>
                  {t.label && <div style={{ fontSize: 12, color: 'var(--color-neutral-700)', marginBottom: 6, lineHeight: 1.4 }}>{t.label}</div>}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{ flex: 1, fontSize: 12, background: 'var(--color-neutral-50)', borderRadius: 4, padding: '4px 8px', color: 'var(--color-neutral-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tokenUrl(t.token)}
                    </code>
                    <button className="btn btn-secondary btn-sm" style={{ gap: 3, flexShrink: 0, fontSize: 12 }} onClick={() => copyUrl(t)}>
                      {copiedId === t.recordId ? <IconCheck size={11} /> : <IconCopy size={11} />}
                      {copiedId === t.recordId ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ gap: 3, flexShrink: 0, fontSize: 12, color: 'var(--cds-red-700)' }}
                      onClick={() => handleRevoke(t)}
                      disabled={revoking === t.recordId}
                    >
                      {revoking === t.recordId ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <IconX size={11} />}
                      Revoke
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 6 }}>
                    Expires in {daysUntil(t.expiresAt)} days · Created by {t.createdBy}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Revoked links */}
          {!loading && inactive.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Revoked / Expired</div>
              {inactive.map(t => (
                <div key={t.recordId} style={{ border: '1px solid var(--color-neutral-100)', borderRadius: 4, padding: '8px 12px', marginBottom: 6, opacity: 0.6 }}>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{t.label || '(no instructions)'} · expired/revoked</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────

export default function DocumentsTab({ requestId, actorEmail, requesterEmail, requesterName, requestNumber }: Props) {
  const [docs, setDocs]       = useState<FoiaDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors]     = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [releaseUrl, setReleaseUrl]   = useState<string | null>(null);
  const [releaseCopied, setReleaseCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    getDocumentsForRequest(requestId)
      .then(setDocs)
      .finally(() => setLoading(false));
  }, [requestId]);

  async function ensureReleaseUrl(): Promise<string> {
    const { token } = await getOrCreateReleaseToken(requestId, actorEmail ?? '');
    const url = buildReleaseUrl(token.token);
    setReleaseUrl(url);
    return url;
  }

  async function handleDocReleased(releasedDoc: FoiaDocument) {
    try {
      const { token, isNew } = await getOrCreateReleaseToken(requestId, actorEmail ?? '');
      const url = buildReleaseUrl(token.token);
      setReleaseUrl(url);

      // Only send correspondence on the very first release for this request
      if (isNew && requesterEmail) {
        await sendCorrespondence({
          requestId,
          direction:   'outbound',
          toAddresses: [requesterEmail],
          fromUserId:  actorEmail,
          subject:     `Records Available — Request ${requestNumber ?? requestId}`,
          body: `Dear ${requesterName ?? 'Requester'},

Your public records request (Request No. ${requestNumber ?? requestId}) has been fulfilled. Responsive records are now available for download at the secure link below:

${url}

This link is unique to your request. Please save it for your records — it remains valid for 365 days.

Some records may have been redacted pursuant to applicable exemptions under the Freedom of Information Act. The versions available at the link above are the official released copies.

If you have any questions, please contact us and reference your request number.

Sincerely,
Records Office`,
          isAutomated: true,
        }, actorEmail);
      }
    } catch (err) {
      console.error('Release token/correspondence error:', err);
    }
  }

  async function uploadFiles(files: FileList | File[]) {
    const fileArray = Array.from(files);
    if (!fileArray.length) return;
    setUploading(true);
    setUploadErrors([]);
    for (const file of fileArray) {
      setUploadProgress(p => ({ ...p, [file.name]: 0 }));
      try {
        const doc = await uploadAndCreateDocument({
          requestId,
          file,
          source: 'staff',
          uploadedByEmail: actorEmail ?? '',
          onProgress: pct => setUploadProgress(p => ({ ...p, [file.name]: pct })),
        });
        setDocs(d => [doc, ...d]);
        await createAuditEntry({
          requestId,
          userEmail: actorEmail,
          action: 'document.uploaded',
          details: JSON.stringify({ fileName: file.name, fileSize: file.size }),
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('Upload failed for', file.name, err);
        setUploadErrors(e => [...e, `${file.name}: ${msg}`]);
      } finally {
        setUploadProgress(p => { const n = { ...p }; delete n[file.name]; return n; });
      }
    }
    setUploading(false);
  }

  // When docs are already released and we don't yet have the URL, load it.
  // Must be before any early return to satisfy Rules of Hooks.
  const releasedCount = docs.filter(d => d.source !== 'requester' && d.isReleased).length;
  useEffect(() => {
    if (releasedCount > 0 && !releaseUrl) {
      ensureReleaseUrl().catch(console.error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releasedCount]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
        <IconLoader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const pending = Object.entries(uploadProgress);
  const requesterDocs = docs.filter(d => d.source === 'requester');
  const staffDocs     = docs.filter(d => d.source !== 'requester');
  const responsive = staffDocs.filter(d => d.responsiveClass === 'responsive' || d.responsiveClass === 'partiallyResponsive');
  const released   = staffDocs.filter(d => d.isReleased);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-700)' }}>
          {staffDocs.length} case document{staffDocs.length !== 1 ? 's' : ''} · {responsive.length} responsive · {released.length} released
          {requesterDocs.length > 0 && <span style={{ color: 'var(--cds-yellow-600)', marginLeft: 8 }}>· {requesterDocs.length} requester-submitted</span>}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary btn-sm"
            style={{ gap: 4 }}
            onClick={() => setShowLinks(true)}
          >
            <IconLink size={12} /> Upload Links
          </button>
          <button
            className="btn btn-secondary btn-sm"
            style={{ gap: 4 }}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <IconUpload size={12} /> Upload
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files) uploadFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {showLinks && (
        <UploadLinksModal
          requestId={requestId}
          actorEmail={actorEmail ?? ''}
          onClose={() => setShowLinks(false)}
        />
      )}

      {/* Release link banner */}
      {releaseUrl && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          padding: '10px 14px', background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 7,
        }}>
          <IconLink size={14} style={{ color: 'var(--cds-green-700)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cds-green-700)', marginBottom: 2 }}>Release link generated</div>
            <div style={{ fontSize: 12, color: 'var(--cds-green-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{releaseUrl}</div>
          </div>
          <button
            className="btn btn-sm btn-secondary"
            style={{ flexShrink: 0, gap: 4 }}
            onClick={async () => {
              await navigator.clipboard.writeText(releaseUrl);
              setReleaseCopied(true);
              setTimeout(() => setReleaseCopied(false), 2000);
            }}
          >
            {releaseCopied ? <><IconCheck size={12} /> Copied!</> : <><IconCopy size={12} /> Copy Link</>}
          </button>
        </div>
      )}

      {/* Drop zone (visible when empty or dragging) */}
      {(docs.length === 0 || dragOver) && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? 'var(--color-primary-500)' : 'var(--color-neutral-200)'}`,
            borderRadius: 4, padding: '32px', textAlign: 'center',
            cursor: 'pointer', marginBottom: 16, transition: 'border-color 0.15s',
            background: dragOver ? 'var(--color-primary-50, var(--cds-blurple-50))' : 'transparent',
          }}
        >
          <IconUpload size={24} style={{ color: 'var(--color-neutral-300)', marginBottom: 8 }} />
          <div style={{ fontSize: 14, color: 'var(--color-neutral-500)' }}>
            Drop files here or <span style={{ color: 'var(--color-primary-700)', fontWeight: 500 }}>click to upload</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 4 }}>
            PDF, Word, images, spreadsheets — any file type
          </div>
        </div>
      )}

      {/* In-progress uploads */}
      {pending.map(([name, pct]) => (
        <div key={name} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
          background: 'var(--cds-green-50)', borderRadius: 4, marginBottom: 6,
        }}>
          <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--cds-green-700)' }} />
          <span style={{ fontSize: 12, color: 'var(--cds-green-700)', flex: 1 }}>{name}</span>
          <span style={{ fontSize: 12, color: 'var(--cds-green-700)' }}>{pct}%</span>
        </div>
      ))}

      {/* Upload errors */}
      {uploadErrors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {uploadErrors.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '8px 12px', background: 'var(--cds-red-50)', borderRadius: 4,
              marginBottom: 4, border: '1px solid #F7D5D0',
            }}>
              <IconAlertCircle size={14} style={{ color: 'var(--cds-red-700)', marginTop: 1, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--cds-red-700)' }}>{msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* Requester-submitted documents */}
      {requesterDocs.length > 0 && (
        <div style={{ border: '1px solid #FCD8A3', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
            background: 'var(--cds-yellow-50)', borderBottom: '1px solid #FCD8A3',
          }}>
            <IconUpload size={13} style={{ color: 'var(--cds-yellow-600)' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cds-yellow-800)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Submitted by Requester
            </span>
            <span style={{ fontSize: 12, color: 'var(--cds-yellow-700)', marginLeft: 4 }}>
              {requesterDocs.length} file{requesterDocs.length !== 1 ? 's' : ''} — review only, not for release
            </span>
          </div>
          {requesterDocs.map(doc => (
            <DocRow
              key={doc.recordId}
              doc={doc}
              actorEmail={actorEmail}
              requesterSubmitted
              onUpdate={updated => setDocs(ds => ds.map(d => d.recordId === updated.recordId ? updated : d))}
              onDelete={id => setDocs(ds => ds.filter(d => d.recordId !== id))}
            />
          ))}
        </div>
      )}

      {/* Case documents */}
      {staffDocs.length > 0 && (
        <div style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden' }}>
          {requesterDocs.length > 0 && (
            <div style={{
              display: 'flex', gap: 16, padding: '7px 14px',
              background: 'var(--color-neutral-50)',
              borderBottom: '1px solid var(--color-neutral-100)',
            }}>
              <div style={{ width: 20 }} />
              <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Case Documents</div>
            </div>
          )}
          {!requesterDocs.length && (
            <div style={{
              display: 'flex', gap: 16, padding: '7px 14px',
              background: 'var(--color-neutral-50)',
              borderBottom: '1px solid var(--color-neutral-100)',
            }}>
              <div style={{ width: 20 }} />
              <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>File</div>
              <div style={{ width: 160, fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Classification</div>
              <div style={{ width: 80 }} />
            </div>
          )}
          {staffDocs.map(doc => (
            <DocRow
              key={doc.recordId}
              doc={doc}
              actorEmail={actorEmail}
              onUpdate={updated => setDocs(ds => ds.map(d => d.recordId === updated.recordId ? updated : d))}
              onDelete={id => setDocs(ds => ds.filter(d => d.recordId !== id))}
              onReleased={handleDocReleased}
            />
          ))}
        </div>
      )}

      {/* Exemption codes hint */}
      {responsive.length > 0 && released.length < responsive.length && (
        <div style={{
          marginTop: 12, padding: '8px 12px',
          background: 'var(--cds-yellow-50)', borderRadius: 4,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <IconAlertCircle size={14} style={{ color: 'var(--cds-yellow-500)', marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--cds-yellow-500)' }}>
            {responsive.length - released.length} responsive document{responsive.length - released.length !== 1 ? 's' : ''} not yet released. Use the Release button to make them available to the requester.
          </span>
        </div>
      )}
    </div>
  );
}
